import React, { useState } from "react";
import {
  ListItem,
  ListItemButton,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; // Import useTheme
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

type Chat = {
  id: string;
  name: string;
};

type ChatItemProps = {
  chat: Chat;
  activeChat: string | null;
  setActiveChat: (chatId: string) => void;
  onEditChat: (chatId: string) => void;
  onFeedbackChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  isGenerating: boolean;
};

const ChatItem: React.FC<ChatItemProps> = ({
  chat,
  activeChat,
  setActiveChat,
  onEditChat,
  onFeedbackChat,
  onDeleteChat,
  isGenerating,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isSelected = activeChat === chat.id;

  return (
    // Wrap with ListItem to apply margin/padding correctly within a List
    <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
      <ListItemButton
        selected={isSelected}
        onClick={() => !isGenerating && setActiveChat(chat.id)}
        disabled={isGenerating}
        sx={{
          borderRadius: "8px",
          position: "relative", // Needed for the indicator bar
          // Default state
          "&:hover": {
            backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.action.hover,
          },
          // New styling for the selected item
          "&.Mui-selected": {
            backgroundColor: theme.palette.action.selected,
            // The vertical indicator bar
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "15%",
              height: "70%",
              width: "3px",
              backgroundColor: "primary.main",
              borderRadius: "0 3px 3px 0",
            },
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            // Use the isSelected flag for bolding
            fontWeight: isSelected ? "600" : "400",
          }}
        >
          {chat.name}
        </Typography>

        {/* Show menu only when item is selected for a cleaner UI */}
        {isSelected && (
            <IconButton
                onClick={handleMenuOpen}
                sx={{ ml: 1, color: "text.secondary" }}
                aria-label="More actions"
            >
                <MoreHorizOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem
            onClick={() => {
              onEditChat(chat.id);
              handleMenuClose();
            }}
          >
            <EditOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2" color="text.primary">
              แก้ไขชื่อห้องสนทนา
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onFeedbackChat(chat.id);
              handleMenuClose();
            }}
          >
            <FeedbackOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2" color="text.primary">
              ให้คำติชม
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDeleteChat(chat.id);
              handleMenuClose();
            }}
          >
            <DeleteOutlinedIcon
              sx={{ mr: 1, fontSize: 16, color: "error.main" }}
            />
            <Typography variant="body2" color="error.main">
              ลบห้องสนทนา
            </Typography>
          </MenuItem>
        </Menu>
      </ListItemButton>
    </ListItem>
  );
};

export default ChatItem;